import type { CSSProperties } from "react";
import type { MediaData } from "@/db/schema/hiive";

/**
 * The desktop frame shared by the hiive, event, venue and venue-experience
 * pages. Applies from the `wide:` variant (globals.css): a desktop, and any
 * landscape window from `md` — iPhone Duo's inner display reads as two pages,
 * media on one side of the fold and content on the other.
 *
 * On a phone each page keeps its own layout — the hero-plus-drawer of hiives
 * and events, the scrolling column of venues. From `wide` they all become the
 * same page: one top bar, a sticky media column on the left carrying the
 * primary action, and the content on the right. Every class here is
 * `wide:`-prefixed so spreading it into a phone layout changes nothing there.
 *
 * Pair with `useIsDesktop()` wherever a component has to MOUNT differently
 * (a vaul drawer versus an inline column); these only say where things sit.
 */
export const DETAIL_DESKTOP = {
  /** Page grid — media (5) beside content (7), clear of the top bar. */
  grid: "detail-split wide:mx-auto wide:grid wide:h-auto wide:min-h-dvh wide:w-full wide:max-w-6xl wide:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] wide:items-start wide:gap-12 wide:px-8 wide:pt-[calc(env(safe-area-inset-top)+5.5rem)] wide:pb-16 side-nav:wide:pt-[calc(env(safe-area-inset-top)+1.5rem)]",
  /**
   * The sticky media column. Width is capped so the media frame plus the
   * action card under it still fits on a short laptop screen instead of
   * running past the fold, where a sticky column can never be scrolled to.
   * The cap scales with the frame's shape (`detailMediaAspectStyle`): a tall
   * 9:16 clip gets a narrower column than a 16:10 venue photo. In the
   * system column (`side-nav:`) there is no top bar to clear, so the reserve
   * shrinks from 15rem to 9rem: at 15rem a closed, rotated Duo (466pt tall)
   * got a ~180pt column whose action button wrapped.
   */
  mediaColumn:
    "detail-split-media wide:sticky wide:top-[calc(env(safe-area-inset-top)+5.5rem)] side-nav:wide:top-[calc(env(safe-area-inset-top)+1.5rem)] wide:flex wide:w-full wide:max-w-[calc((100dvh-15rem)*var(--detail-media-aspect,0.8))] wide:flex-col wide:gap-4 side-nav:wide:max-w-[calc((100dvh-9rem)*var(--detail-media-aspect,0.8))]",
  /** The media frame inside that column. */
  media: "wide:max-h-none wide:overflow-hidden wide:rounded-3xl",
  /**
   * Media aspect on desktop. Follows the media itself (set through
   * `detailMediaAspectStyle` on the column) and falls back to 4:5.
   */
  mediaAspect: "wide:aspect-[var(--detail-media-aspect,0.8)]",
  /** Content column. `min-w-0` keeps wide rails from blowing out the grid. */
  content: "wide:min-w-0",
  /**
   * Top bar: solid on desktop because the media no longer sits under it, so
   * there is nothing for a transparent bar to float over.
   */
  barSurface:
    "wide:pointer-events-auto wide:border-border/60 wide:bg-background/85 wide:py-3 wide:backdrop-blur-md dark:wide:border-glass-border-10",
  /** Top bar row, aligned to the page grid rather than the screen corners. */
  barInner: "wide:mx-auto wide:w-full wide:max-w-6xl wide:px-8",
  /**
   * Round back/share buttons on that bar. The phone's `bg-black/40` glass is
   * for sitting on a photo; on a plain bar it reads as a grey hole.
   */
  barButton:
    "wide:bg-surface-alt wide:text-text-primary wide:shadow-none wide:backdrop-blur-none wide:hover:bg-surface-elevated dark:wide:bg-glass-bg-08 dark:wide:hover:bg-glass-bg-10",
  /**
   * The primary action as a card under the media, replacing the floating
   * pill a phone gets pinned to the bottom of the screen.
   */
  /**
   * iPhone Duo's system column (`side-nav:`, globals.css): a floating
   * back/share bar becomes that column — the buttons stacked below the
   * status bar, where iOS puts a navigation bar's buttons on that device —
   * and the page grid no longer clears a top bar. `barColumn` goes on the
   * fixed bar, `barColumnRow` on its row, `barColumnButton` on each round
   * button (a plain surface: no photo sits under the column), `barTitle` on
   * the name that scrolls into the bar (no room for it in ~84pt). A bar that
   * is a real title bar (the class booking flow) keeps its row instead. No
   * transition: rotating would otherwise animate the bar into the column.
   */
  barColumn:
    "side-nav:inset-y-0 side-nav:w-(--side-nav-width) side-nav:border-0 side-nav:bg-transparent side-nav:p-0 side-nav:pt-[max(var(--side-nav-top),0.75rem)] side-nav:backdrop-blur-none side-nav:transition-none side-nav-left:right-auto side-nav-right:left-auto",
  barColumnRow:
    "side-nav:mx-0 side-nav:max-w-none side-nav:flex-col side-nav:justify-start side-nav:gap-2 side-nav:px-0 side-nav:pt-0",
  barColumnButton:
    "side-nav:ml-0 side-nav:size-11 side-nav:border-0 side-nav:bg-surface-alt side-nav:text-text-primary side-nav:shadow-none side-nav:backdrop-blur-none dark:side-nav:bg-glass-bg-08",
  barTitle: "side-nav:hidden",
  /**
   * iPhone Duo's inner display held upright (`status-corner:`): the bar
   * rises into the top band beside the corner status stack instead of
   * starting below the full-width top inset, its 44pt buttons centred on
   * the stack (4pt low, the time's optical centre).
   */
  barCorner:
    "status-corner:pt-[calc(var(--status-corner-height)*0.5_-_1.125rem)] status-corner-left:pl-[calc(1rem+var(--status-corner-width))] status-corner-right:pr-[calc(1rem+var(--status-corner-width))]",
  actionCard:
    "rounded-[22px] border border-border bg-surface p-3 shadow-sm dark:border-white/[0.1] dark:bg-[#101319]",
} as const;

/** Taller than a story or wider than a TV frame reads as a sliver. */
const MIN_DETAIL_MEDIA_ASPECT = 9 / 16;
const MAX_DETAIL_MEDIA_ASPECT = 16 / 9;

/**
 * The desktop media frame's shape, as a CSS variable for the media column.
 *
 * Taken from the first slide's stored width/height, so a 16:10 venue photo is
 * shown whole instead of being cut down to a 4:5 sliver. Media without stored
 * dimensions falls back to the entity's upload shape (`fallback`), never to a
 * size measured on load (that would snap once the bytes arrive). Only read
 * from `wide`; phones keep their own aspect classes.
 */
export function detailMediaAspectStyle(
  media: readonly Pick<MediaData, "width" | "height">[] | null | undefined,
  fallback: number,
): CSSProperties {
  const first = media?.[0];
  const stored =
    first?.width && first.height ? first.width / first.height : null;
  const ratio = Math.min(
    MAX_DETAIL_MEDIA_ASPECT,
    Math.max(MIN_DETAIL_MEDIA_ASPECT, stored ?? fallback),
  );
  return { "--detail-media-aspect": ratio.toFixed(4) } as CSSProperties;
}
